import { loadWaveform, type WaveformResult } from './load'

// Private types ---------------------------------------------------------------

type WaveformTarget = Readonly<{
  audio: HTMLAudioElement
  range: HTMLElement
  src: string
}>

type WaveformRequest = Readonly<{
  target: WaveformTarget
  abort: AbortController
}>

type WaveformLoader = (
  src: string,
  signal: AbortSignal
) => Promise<WaveformResult>

// Public types ----------------------------------------------------------------

export type WaveformController = Readonly<{
  sync: () => void
  disconnect: () => void
  reconnect: () => void
  cleanup: () => void
}>

// Private functions -----------------------------------------------------------

// Reads only the declared direct source; currentSrc can still refer to an old patch.
const readTarget = (root: HTMLElement): WaveformTarget | undefined => {
  const audio = root.querySelector('audio')
  const range = root.querySelector<HTMLElement>('media-time-range')
  const enabled = root.hasAttribute('data-waveform')

  return enabled && audio?.getAttribute('src')?.trim() && range
    ? { audio, range, src: audio.src }
    : undefined
}

// Includes element identity so replacing a retained timeline restarts its enhancement.
const targetsMatch = (
  left: WaveformTarget | undefined,
  right: WaveformTarget | undefined
): boolean =>
  left?.audio === right?.audio &&
  left?.range === right?.range &&
  left?.src === right?.src

// Restores the ordinary progress track without touching native playback state.
const clearWaveform = (range: HTMLElement): void => {
  range.removeAttribute('data-waveform-ready')
  range.style.removeProperty('--audio-waveform-mask')
}

// Applies decoration only if this request still owns the current source and range.
const applyWaveform = async (
  root: HTMLElement,
  request: WaveformRequest,
  load: WaveformLoader
): Promise<void> => {
  try {
    const result = await load(request.target.src, request.abort.signal)

    if (
      request.abort.signal.aborted ||
      !targetsMatch(request.target, readTarget(root))
    ) {
      return
    }

    if (result.status === 'ready') {
      request.target.range.style.setProperty(
        '--audio-waveform-mask',
        result.mask
      )
      request.target.range.setAttribute('data-waveform-ready', '')
    }
  } catch {
    // A rejected optional loader must never affect the usable seek control.
  }
}

// Public functions ------------------------------------------------------------

// Owns one optional download and its decoration across patches and disconnection.
export const createWaveformController = (
  root: HTMLElement,
  load: WaveformLoader = loadWaveform
): WaveformController => {
  let lifecycle: 'connected' | 'disconnected' | 'destroyed' = 'connected'
  let request: WaveformRequest | undefined

  // Ends the previous request's lifetime before another target acquires the range.
  const reset = (): void => {
    request?.abort.abort()

    if (request) {
      clearWaveform(request.target.range)
    }

    request = undefined
  }

  // Leaves unchanged targets alone and reconciles only a changed source or owner.
  const sync = (): void => {
    if (lifecycle !== 'connected') {
      return
    }

    const target = readTarget(root)

    if (targetsMatch(target, request?.target)) {
      return
    }

    reset()

    if (target) {
      request = { target, abort: new AbortController() }
      applyWaveform(root, request, load)
    }
  }

  // Native source changes can arrive before the next LiveView updated callback.
  root.addEventListener('loadstart', sync, true)
  sync()

  return {
    sync,
    disconnect: () => {
      if (lifecycle === 'connected') {
        lifecycle = 'disconnected'
        reset()
      }
    },
    reconnect: () => {
      if (lifecycle === 'disconnected') {
        lifecycle = 'connected'
        sync()
      }
    },
    cleanup: () => {
      lifecycle = 'destroyed'
      root.removeEventListener('loadstart', sync, true)
      reset()
    }
  }
}
